import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Send, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  useCreatePurchaseRequisition,
  usePurchaseRequisition,
  useSubmitPurchaseRequisition,
  useUpdatePurchaseRequisition,
} from '@/hooks/use-procurement';
import { useProjects } from '@/hooks/use-projects';
import { useCostCodes } from '@/hooks/use-payroll';
import { usePlatformModules } from '@/hooks/use-modules';
import { useOrgCurrency } from '@/hooks/use-settings';
import { apiClient } from '@/lib/api-client';
import { formatMoney } from '@/lib/currency';
import { formErrorText } from '@/lib/form-errors';
import type {
  PurchaseRequisition,
  PurchaseRequisitionLinePayload,
  PurchaseRequisitionPriority,
} from '@/lib/types';
import { estimatedTotal, priorityLabels } from './requisition-shared';

interface LineDraft {
  description: string;
  quantity: string;
  estimated_unit_cost: string;
  // Catalog metadata is not editable in this form but must round-trip so an
  // unrelated edit does not blank it (the API replaces all lines on PATCH).
  gear_model: number | null;
  asset_category: string;
}

const blankLine = (): LineDraft => ({
  description: '',
  quantity: '1',
  estimated_unit_cost: '',
  gear_model: null,
  asset_category: '',
});

interface FormState {
  description: string;
  justification: string;
  project: string;
  cost_code: string;
  needed_by: string;
  priority: PurchaseRequisitionPriority;
}

const emptyForm = (): FormState => ({
  description: '',
  justification: '',
  project: '',
  cost_code: '',
  needed_by: '',
  priority: 'normal',
});

const FIELD_LABELS: Record<string, string> = {
  description: 'Description',
  justification: 'Justification',
  project: 'Project',
  cost_code: 'Cost code',
  needed_by: 'Needed by',
  priority: 'Priority',
  lines: 'Line',
};

function describeError(
  err: unknown,
  sentLines: PurchaseRequisitionLinePayload[],
  fallback: string
): string {
  // Blank lines are left out of the request, so name a failing line by its
  // description rather than by its position in the request.
  return formErrorText(err, fallback, FIELD_LABELS, (listField, index) =>
    listField === 'lines' && sentLines[index]
      ? `Line "${sentLines[index].description}"`
      : undefined
  );
}

export function RequisitionFormPage() {
  const orgCurrency = useOrgCurrency();
  const { id } = useParams<{ id: string }>();
  const reqId = id ? Number(id) : 0;
  const isEdit = reqId > 0;
  const navigate = useNavigate();

  const { isModuleEnabled } = usePlatformModules();
  const payrollEnabled = isModuleEnabled('payroll');

  const { data: existing, isLoading: loadingExisting } = usePurchaseRequisition(reqId);
  const { data: projectsData } = useProjects({ page_size: 200, status: 'active' });
  const { data: costCodes } = useCostCodes(payrollEnabled ? { is_active: true } : {});

  const createMut = useCreatePurchaseRequisition();
  const updateMut = useUpdatePurchaseRequisition();
  const submitMut = useSubmitPurchaseRequisition();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // Set once a create succeeds, so a retry after a failed submit updates that
  // draft instead of creating another one.
  const [createdId, setCreatedId] = useState<number | null>(null);
  const draftId = isEdit ? reqId : createdId;

  useEffect(() => {
    if (!isEdit || !existing || hydrated) return;
    setForm({
      description: existing.description,
      justification: existing.justification,
      project: existing.project ? String(existing.project) : '',
      cost_code: existing.cost_code ? String(existing.cost_code) : '',
      needed_by: existing.needed_by ?? '',
      priority: existing.priority,
    });
    setLines(
      existing.lines.length
        ? existing.lines.map((l) => ({
            description: l.description,
            quantity: l.quantity,
            estimated_unit_cost: l.estimated_unit_cost ?? '',
            gear_model: l.gear_model,
            asset_category: l.asset_category,
          }))
        : [blankLine()]
    );
    setHydrated(true);
  }, [isEdit, existing, hydrated]);

  const updateLine = (idx: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const total = estimatedTotal(lines);
  const busy = createMut.isPending || updateMut.isPending || submitMut.isPending;

  const buildPayload = () => {
    const cleanedLines: PurchaseRequisitionLinePayload[] = lines
      .filter((l) => l.description.trim() !== '')
      .map((l, i) => ({
        line_number: i + 1,
        description: l.description.trim(),
        quantity: l.quantity || '1',
        estimated_unit_cost: l.estimated_unit_cost === '' ? null : l.estimated_unit_cost,
        gear_model: l.gear_model,
        asset_category: l.asset_category,
      }));
    return {
      description: form.description.trim(),
      justification: form.justification,
      project: form.project ? Number(form.project) : null,
      cost_code: form.cost_code ? Number(form.cost_code) : null,
      needed_by: form.needed_by || null,
      priority: form.priority,
      lines: cleanedLines,
    };
  };

  const save = async (andSubmit: boolean) => {
    setError(null);
    if (!form.description.trim()) {
      setError('Description is required');
      return;
    }
    const payload = buildPayload();
    if (andSubmit && payload.lines.length === 0) {
      setError('Add at least one line item before submitting');
      return;
    }
    let saved: PurchaseRequisition;
    try {
      saved = draftId
        ? await updateMut.mutateAsync({ id: draftId, data: payload })
        : await createMut.mutateAsync(payload);
    } catch (err) {
      setError(
        describeError(
          err,
          payload.lines,
          draftId ? 'Failed to update requisition' : 'Failed to create requisition'
        )
      );
      return;
    }
    if (!draftId) setCreatedId(saved.id);
    if (andSubmit) {
      try {
        await submitMut.mutateAsync(saved.id);
      } catch (err) {
        // The submit may have gone through with only its response lost; if so
        // the requisition is no longer a draft and a retry could not edit it.
        const current = await apiClient
          .get<PurchaseRequisition>(`/api/v1/purchase-requisitions/${saved.id}/`)
          .catch(() => null);
        if (current && current.data.status !== 'draft') {
          navigate(`/requisitions/${saved.id}`);
          return;
        }
        setError(
          `Saved as draft PR-${saved.id}, but it could not be submitted. ${describeError(
            err,
            payload.lines,
            'Please try again.'
          )}`
        );
        return;
      }
    }
    navigate(`/requisitions/${saved.id}`);
  };

  if (isEdit && loadingExisting) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (isEdit && existing && existing.status !== 'draft') {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-700">Only draft requisitions can be edited.</p>
        <Button asChild className="mt-4" variant="outline">
          <Link to={`/requisitions/${reqId}`}>Back to requisition</Link>
        </Button>
      </div>
    );
  }

  const projects = projectsData?.results ?? [];
  const backHref = isEdit ? `/requisitions/${reqId}` : '/requisitions';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backHref}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Link>
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? `Edit Requisition PR-${reqId}` : 'New Requisition'}
        </h1>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save(false);
        }}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>What do you need?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Replacement ropes for Lions Gate crew"
                required
              />
            </div>
            <div>
              <Label htmlFor="justification">Justification</Label>
              <Textarea
                id="justification"
                value={form.justification}
                onChange={(e) => setForm({ ...form, justification: e.target.value })}
                placeholder="Why is this needed? What happens if it isn't approved?"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project">Project</Label>
                <select
                  id="project"
                  value={form.project}
                  onChange={(e) => setForm({ ...form, project: e.target.value })}
                  className="mt-1 flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                >
                  <option value="">— None —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_number ? `${p.project_number} · ` : ''}
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              {payrollEnabled && (
                <div>
                  <Label htmlFor="cost_code">Cost Code</Label>
                  <select
                    id="cost_code"
                    value={form.cost_code}
                    onChange={(e) => setForm({ ...form, cost_code: e.target.value })}
                    className="mt-1 flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                  >
                    <option value="">— None —</option>
                    {(costCodes ?? []).map((cc) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.code} — {cc.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label htmlFor="priority">Priority</Label>
                <select
                  id="priority"
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value as PurchaseRequisitionPriority })
                  }
                  className="mt-1 flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                >
                  {(Object.keys(priorityLabels) as PurchaseRequisitionPriority[]).map((p) => (
                    <option key={p} value={p}>
                      {priorityLabels[p]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="needed_by">Needed By</Label>
                <Input
                  id="needed_by"
                  type="date"
                  value={form.needed_by}
                  onChange={(e) => setForm({ ...form, needed_by: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Line Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              <Plus className="h-4 w-4 mr-1" />
              Add Line
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
              <div className="col-span-6">Description</div>
              <div className="col-span-2">Qty</div>
              <div className="col-span-3">Est. Unit Cost</div>
              <div className="col-span-1" />
            </div>
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-12 sm:col-span-6">
                  <Input
                    value={line.description}
                    onChange={(e) => updateLine(idx, { description: e.target.value })}
                    placeholder="Item description"
                    aria-label={`Line ${idx + 1} description`}
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={line.quantity}
                    onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                    aria-label={`Line ${idx + 1} quantity`}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.estimated_unit_cost}
                    onChange={(e) => updateLine(idx, { estimated_unit_cost: e.target.value })}
                    placeholder="optional"
                    aria-label={`Line ${idx + 1} estimated unit cost`}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(idx)}
                    disabled={lines.length === 1}
                    aria-label={`Remove line ${idx + 1}`}
                  >
                    <Trash2 className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-2 border-t text-sm">
              <span className="text-gray-600 mr-2">Estimated total:</span>
              <span className="font-semibold">{formatMoney(total, orgCurrency)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" asChild>
            <Link to={backHref}>Cancel</Link>
          </Button>
          <Button type="submit" variant="outline" disabled={busy}>
            {busy ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button type="button" onClick={() => void save(true)} disabled={busy}>
            <Send className="h-4 w-4 mr-1" />
            {isEdit ? 'Save & Submit' : 'Submit for Approval'}
          </Button>
        </div>
      </form>
    </div>
  );
}
